/**
 * Drives the arrow/drag slider for the "You may also like" carousel that
 * renders inside the cart drawer (snippets/cart-drawer-recommendations.liquid).
 *
 * This MUST be loaded as a real asset from snippets/scripts.liquid, not as
 * an inline <script> inside the cart-drawer-recommendations snippet itself.
 * sections/header.liquid hydrates the entire header section (which is
 * where the cart drawer lives) on every page load via
 * `@theme/section-hydration`'s `hydrate()`, which fetches the section's
 * real markup and merges it in using the same DOM morphing the theme uses
 * for cart updates (morph.js). Browsers never execute <script> tags that
 * arrive as part of morphed/injected HTML, and since that hydration pass
 * runs on every page load (not just after a cart change), an inline
 * <script> anywhere inside the header section never runs - not even once.
 * That's why the arrows always looked right but never responded. Loading
 * this file from scripts.liquid - which renders in the page's static
 * <head> shell, never touched by hydration or morphing - is the only
 * place this logic can reliably run.
 *
 * Binding every listener at the `document` level (rather than to the
 * arrow buttons/track directly) also means this keeps working no matter
 * how many times the cart drawer's contents get morphed afterwards (e.g.
 * on every add/remove/quantity change).
 */
(function cartDrawerRecommendationsSlider() {
  if (window.__mvCartRecsSliderBound) return;
  window.__mvCartRecsSliderBound = true;

  var dragState = new WeakMap();

  function getStep(track) {
    var item = track.querySelector('.cart-drawer-recommendations__item');
    if (!item) return 0;
    var trackStyle = getComputedStyle(track);
    var gap = parseFloat(trackStyle.columnGap) || parseFloat(trackStyle.gap) || 16;
    return item.getBoundingClientRect().width + gap;
  }

  function getMaxIndex(container, track) {
    var viewport = container.querySelector('.cart-drawer-recommendations__viewport');
    var items = track.querySelectorAll('.cart-drawer-recommendations__item').length;
    var step = getStep(track);
    if (!viewport || !step) return 0;
    var visible = Math.max(1, Math.floor(viewport.clientWidth / step));
    return Math.max(0, items - visible);
  }

  function setTransform(track, px, animate) {
    track.style.transition = animate ? 'transform 0.3s ease' : 'none';
    track.style.transform = 'translateX(' + px + 'px)';
  }

  function getIndex(track) {
    return Number(track.dataset.sliderIndex || 0);
  }

  function update(container, track, index, animate) {
    var step = getStep(track);
    var maxIndex = getMaxIndex(container, track);
    index = Math.min(Math.max(index, 0), maxIndex);
    track.dataset.sliderIndex = index;
    setTransform(track, -index * step, animate !== false);
  }

  function refreshAll(animate) {
    document.querySelectorAll('.cart-drawer-recommendations__list').forEach(function (track) {
      var container = track.closest('.cart-drawer-recommendations');
      if (container) update(container, track, getIndex(track), animate);
    });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('.cart-drawer-recommendations__arrow');
    if (!button) return;
    var container = button.closest('.cart-drawer-recommendations');
    var track = container && container.querySelector('.cart-drawer-recommendations__list');
    if (!track) return;
    update(container, track, getIndex(track) + Number(button.dataset.direction), true);
  });

  document.addEventListener('pointerdown', function (event) {
    var track = event.target.closest('.cart-drawer-recommendations__list');
    if (!track) return;
    // Don't hijack presses that start on an interactive control (the add-to-cart
    // button, the product link, etc). setPointerCapture below redirects every
    // subsequent pointer event - including the click the browser synthesizes on
    // release - to the track itself, which silently ate clicks on the add-to-cart
    // button (the click landed on the track instead of the button, so the form
    // never submitted and nothing was added to the cart).
    if (event.target.closest('a, button, input, product-form-component, add-to-cart-component')) return;
    dragState.set(track, {
      startX: event.clientX,
      startTranslate: -getIndex(track) * getStep(track),
    });
    track.setPointerCapture(event.pointerId);
  });

  document.addEventListener('pointermove', function (event) {
    var track = event.target.closest('.cart-drawer-recommendations__list');
    var state = track && dragState.get(track);
    if (!state) return;
    var delta = event.clientX - state.startX;
    setTransform(track, state.startTranslate + delta, false);
  });

  function endDrag(event) {
    var track = event.target.closest('.cart-drawer-recommendations__list');
    var state = track && dragState.get(track);
    if (!state) return;
    dragState.delete(track);
    var container = track.closest('.cart-drawer-recommendations');
    var delta = event.clientX - state.startX;
    var step = getStep(track);
    var index = getIndex(track);
    if (Math.abs(delta) > step / 4) {
      index += delta < 0 ? 1 : -1;
    }
    update(container, track, index, true);
  }

  document.addEventListener('pointerup', endDrag);
  document.addEventListener('pointercancel', endDrag);

  window.addEventListener('resize', function () {
    refreshAll(false);
  });

  // Position whichever carousel(s) already exist right now.
  refreshAll(false);

  // The cart drawer content is morphed in fresh on every hydration pass
  // and again on every cart update. Re-run positioning whenever the
  // drawer's items wrapper changes so a freshly-morphed carousel starts at
  // a valid, in-range position instead of wherever its brand-new
  // (unstyled) transform defaults to.
  var cartItemsObserver = new MutationObserver(function () {
    refreshAll(false);
  });

  document.querySelectorAll('cart-items-component').forEach(function (el) {
    cartItemsObserver.observe(el, { childList: true, subtree: true });
  });
})();